/**
 * Registrant (WHOIS owner) information form (DEV-121). Collected once before a
 * user's first domain purchase — the domain is registered in THEIR name via a
 * per-user Spaceship contact, so legal ownership sits with the buyer, not Dunea.
 * Editable later. Presentation + client validation only; the server re-validates
 * and creates/reuses the Spaceship contact.
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { apiSaveRegistrant, type RegistrantInfo } from '~/lib/api/domains';
import { SUBDIVISIONS } from './subdivisions';

// Curated ISO-3166 country list (covers the vast majority of buyers). `code` is the
// 2-letter code the registrar requires; `dial` is the phone country code.
const COUNTRIES: { code: string; name: string; dial: string }[] = [
  { code: 'IN', name: 'India', dial: '+91' },
  { code: 'US', name: 'United States', dial: '+1' },
  { code: 'GB', name: 'United Kingdom', dial: '+44' },
  { code: 'CA', name: 'Canada', dial: '+1' },
  { code: 'AU', name: 'Australia', dial: '+61' },
  { code: 'DE', name: 'Germany', dial: '+49' },
  { code: 'FR', name: 'France', dial: '+33' },
  { code: 'NL', name: 'Netherlands', dial: '+31' },
  { code: 'IE', name: 'Ireland', dial: '+353' },
  { code: 'ES', name: 'Spain', dial: '+34' },
  { code: 'IT', name: 'Italy', dial: '+39' },
  { code: 'SE', name: 'Sweden', dial: '+46' },
  { code: 'CH', name: 'Switzerland', dial: '+41' },
  { code: 'SG', name: 'Singapore', dial: '+65' },
  { code: 'AE', name: 'United Arab Emirates', dial: '+971' },
  { code: 'JP', name: 'Japan', dial: '+81' },
  { code: 'BR', name: 'Brazil', dial: '+55' },
  { code: 'ZA', name: 'South Africa', dial: '+27' },
  { code: 'NZ', name: 'New Zealand', dial: '+64' },
  { code: 'NG', name: 'Nigeria', dial: '+234' },
];

/** Split a stored "+91.8147456955" into ['+91', '8147456955'] for editing. */
function splitPhone(phone: string): { dial: string; number: string } {
  const m = /^(\+\d{1,4})\.(\d+)$/.exec(phone || '');
  return m ? { dial: m[1], number: m[2] } : { dial: '+91', number: '' };
}

const EMPTY: RegistrantInfo = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  country: 'IN',
  stateProvince: '',
  city: '',
  postalCode: '',
  address1: '',
  organization: '',
  address2: '',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+\d{1,4}\.\d{4,14}$/;

function Field({
  label,
  value,
  onChange,
  placeholder,
  required,
  type = 'text',
  autoComplete,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  required?: boolean;
  type?: string;
  autoComplete?: string;
}) {
  return (
    <label className="block">
      <span className="ui-label">
        {label} {required && <span className="text-danger">*</span>}
      </span>
      <input
        className="ui-input"
        type={type}
        autoComplete={autoComplete}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

export function RegistrantForm({
  initial,
  onSaved,
  onCancel,
}: {
  initial?: RegistrantInfo | null;
  onSaved: (r: RegistrantInfo) => void;
  onCancel?: () => void;
}) {
  const [form, setForm] = useState<RegistrantInfo>({ ...EMPTY, ...(initial ?? {}) });
  const set = (k: keyof RegistrantInfo) => (v: string) => setForm((f) => ({ ...f, [k]: v }));

  // Phone is split into a dial-code selector + national number, then assembled into
  // the registrar's "+CC.number" format — so the user never types the dot.
  const [phoneParts, setPhoneParts] = useState(() => splitPhone(initial?.phone ?? ''));
  const setPhone = (next: Partial<{ dial: string; number: string }>) => {
    const parts = { ...phoneParts, ...next };
    parts.number = parts.number.replace(/\D/g, ''); // digits only
    setPhoneParts(parts);
    setForm((f) => ({ ...f, phone: parts.number ? `${parts.dial}.${parts.number}` : '' }));
  };

  function validate(): string | null {
    const req: (keyof RegistrantInfo)[] = [
      'firstName',
      'lastName',
      'email',
      'phone',
      'country',
      'stateProvince',
      'city',
      'postalCode',
      'address1',
    ];

    for (const k of req) {
      if (!String(form[k] ?? '').trim()) {
        return 'Please fill in all required fields.';
      }
    }

    if (!EMAIL_RE.test(form.email)) {
      return 'Enter a valid email address.';
    }

    if (!PHONE_RE.test(form.phone)) {
      return 'Enter phone as country code + number, e.g. +1.5551234567';
    }

    return null;
  }

  const save = useMutation({
    mutationFn: (payload: RegistrantInfo) => apiSaveRegistrant(payload),
    onSuccess: (r) => {
      toast.success('Registrant information saved.');
      onSaved(r);
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Could not save registrant information.');
    },
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();

    const err = validate();

    if (err) {
      toast.warn(err);
      return;
    }

    save.mutate(form);
  }

  return (
    <form onSubmit={onSubmit}>
      {/* Why we ask */}
      <div className="ui-banner-info mb-4 items-start leading-relaxed">
        <div className="i-ph:shield-check mt-px shrink-0 text-accent" />
        <span>
          Domain registrars require registrant information to legally register a domain in your name. This information
          is securely stored and only used for domain registration.
        </span>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field
          label="First name"
          value={form.firstName}
          onChange={set('firstName')}
          required
          autoComplete="given-name"
        />
        <Field label="Last name" value={form.lastName} onChange={set('lastName')} required autoComplete="family-name" />
        <Field
          label="Email"
          type="email"
          value={form.email}
          onChange={set('email')}
          required
          placeholder="you@example.com"
          autoComplete="email"
        />
        <label className="block">
          <span className="ui-label">
            Phone <span className="text-danger">*</span>
          </span>
          <div className="flex gap-2">
            <div className="relative w-24 shrink-0">
              <select
                className="ui-input appearance-none pr-7"
                aria-label="Phone country code"
                autoComplete="tel-country-code"
                value={phoneParts.dial}
                onChange={(e) => setPhone({ dial: e.target.value })}
              >
                {COUNTRIES.map((c) => (
                  <option key={`${c.code}-${c.dial}`} value={c.dial}>
                    {c.dial}
                  </option>
                ))}
              </select>
              <div className="i-ph:caret-down pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[12px] text-muted" />
            </div>
            <input
              className="ui-input flex-1"
              type="tel"
              inputMode="numeric"
              autoComplete="tel-national"
              value={phoneParts.number}
              placeholder="8147456955"
              onChange={(e) => setPhone({ number: e.target.value })}
            />
          </div>
        </label>

        <label className="block">
          <span className="ui-label">
            Country <span className="text-danger">*</span>
          </span>
          <div className="relative">
            <select
              className="ui-input appearance-none pr-9"
              autoComplete="country"
              value={form.country}
              onChange={(e) =>
                // Reset state — a subdivision code from the old country won't be valid.
                setForm((f) => ({ ...f, country: e.target.value, stateProvince: '' }))
              }
            >
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </select>
            <div className="i-ph:caret-down pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[13px] text-muted" />
          </div>
        </label>

        {SUBDIVISIONS[form.country] ? (
          <label className="block">
            <span className="ui-label">
              State / Province <span className="text-danger">*</span>
            </span>
            <div className="relative">
              <select
                className="ui-input appearance-none pr-9"
                autoComplete="address-level1"
                value={form.stateProvince}
                onChange={(e) => set('stateProvince')(e.target.value)}
              >
                <option value="">Select…</option>
                {SUBDIVISIONS[form.country].map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.name}
                  </option>
                ))}
              </select>
              <div className="i-ph:caret-down pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[13px] text-muted" />
            </div>
          </label>
        ) : (
          <Field
            label="State / Province code"
            value={form.stateProvince}
            onChange={set('stateProvince')}
            required
            placeholder="2-letter code, e.g. CA"
            autoComplete="address-level1"
          />
        )}
        <Field label="City" value={form.city} onChange={set('city')} required autoComplete="address-level2" />
        <Field
          label="Postal / ZIP code"
          value={form.postalCode}
          onChange={set('postalCode')}
          required
          autoComplete="postal-code"
        />

        <div className="sm:col-span-2">
          <Field
            label="Address line 1"
            value={form.address1}
            onChange={set('address1')}
            required
            autoComplete="address-line1"
          />
        </div>
        <div className="sm:col-span-2">
          <Field
            label="Address line 2 (optional)"
            value={form.address2 ?? ''}
            onChange={set('address2')}
            autoComplete="address-line2"
          />
        </div>
        <div className="sm:col-span-2">
          <Field
            label="Organization (optional)"
            value={form.organization ?? ''}
            onChange={set('organization')}
            autoComplete="organization"
          />
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <button className="ui-btn-accent" type="submit" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : initial ? 'Save changes' : 'Save & continue'}
        </button>
        {onCancel && (
          <button type="button" className="ui-btn-ghost" onClick={onCancel}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}
